/**
 * IndexedDB utility for persistent caching across browser sessions
 * Provides a simple key-value store with TTL support
 *
 * Stores:
 * - photoUrls: Cached photo URLs (placeId -> url)
 * - placeStatus: Cached place status data (placeId -> status object)
 * - offlineLists: Full list data for offline access (listId -> list object)
 * - offlinePlaces: Full place data for offline access (placeId -> place object)
 * - offlineMeta: Metadata about offline downloads (key -> meta object)
 */

const DB_NAME = 'VibaryCache';
const DB_VERSION = 2; // Bumped for new stores
const PHOTO_STORE = 'photoUrls';
const STATUS_STORE = 'placeStatus';
const OFFLINE_LISTS_STORE = 'offlineLists';
const OFFLINE_PLACES_STORE = 'offlinePlaces';
const OFFLINE_META_STORE = 'offlineMeta';

let dbInstance = null;
let dbPromise = null;

/**
 * Initialize and return the IndexedDB database instance
 */
function openDB() {
  // Return existing promise if already opening
  if (dbPromise) {
    return dbPromise;
  }

  // Return existing instance if already opened
  if (dbInstance) {
    return Promise.resolve(dbInstance);
  }

  dbPromise = new Promise((resolve, reject) => {
    // Check if IndexedDB is available
    if (!window.indexedDB) {
      console.warn('IndexedDB not supported, falling back to memory-only cache');
      reject(new Error('IndexedDB not supported'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      console.error('IndexedDB error:', event.target.error);
      dbPromise = null;
      reject(event.target.error);
    };

    request.onsuccess = (event) => {
      dbInstance = event.target.result;
      dbPromise = null;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create photo URLs store with placeId as key
      if (!db.objectStoreNames.contains(PHOTO_STORE)) {
        const photoStore = db.createObjectStore(PHOTO_STORE, { keyPath: 'placeId' });
        photoStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // Create place status store with placeId as key
      if (!db.objectStoreNames.contains(STATUS_STORE)) {
        const statusStore = db.createObjectStore(STATUS_STORE, { keyPath: 'placeId' });
        statusStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // Create offline lists store with listId as key
      if (!db.objectStoreNames.contains(OFFLINE_LISTS_STORE)) {
        const listsStore = db.createObjectStore(OFFLINE_LISTS_STORE, { keyPath: 'id' });
        listsStore.createIndex('userId', 'userId', { unique: false });
        listsStore.createIndex('downloadedAt', 'downloadedAt', { unique: false });
      }

      // Create offline places store with placeId as key
      if (!db.objectStoreNames.contains(OFFLINE_PLACES_STORE)) {
        const placesStore = db.createObjectStore(OFFLINE_PLACES_STORE, { keyPath: 'placeId' });
        placesStore.createIndex('downloadedAt', 'downloadedAt', { unique: false });
      }

      // Create offline metadata store
      if (!db.objectStoreNames.contains(OFFLINE_META_STORE)) {
        db.createObjectStore(OFFLINE_META_STORE, { keyPath: 'key' });
      }
    };
  });

  return dbPromise;
}

/**
 * Get a photo URL from IndexedDB cache
 * @param {string} placeId - The place ID to look up
 * @param {number} maxAge - Maximum age in milliseconds (default 7 days)
 * @returns {Promise<string|null>} - The cached URL or null if not found/expired
 */
export async function getCachedPhotoUrl(placeId, maxAge = 7 * 24 * 60 * 60 * 1000) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([PHOTO_STORE], 'readonly');
      const store = transaction.objectStore(PHOTO_STORE);
      const request = store.get(placeId);

      request.onsuccess = () => {
        const result = request.result;
        if (!result) {
          resolve(null);
          return;
        }

        // Check if entry is expired
        const age = Date.now() - result.timestamp;
        if (age > maxAge) {
          // Entry expired, will be cleaned up later
          resolve(null);
          return;
        }

        resolve(result.url);
      };

      request.onerror = () => {
        console.error('Error reading from IndexedDB:', request.error);
        resolve(null); // Return null on error, don't reject
      };
    });
  } catch (error) {
    console.warn('IndexedDB not available:', error);
    return null;
  }
}

/**
 * Store a photo URL in IndexedDB cache
 * @param {string} placeId - The place ID
 * @param {string} url - The photo URL to cache
 * @returns {Promise<void>}
 */
export async function setCachedPhotoUrl(placeId, url) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([PHOTO_STORE], 'readwrite');
      const store = transaction.objectStore(PHOTO_STORE);
      const request = store.put({
        placeId,
        url,
        timestamp: Date.now(),
      });

      request.onsuccess = () => resolve();
      request.onerror = () => {
        console.error('Error writing to IndexedDB:', request.error);
        resolve(); // Don't reject, just log the error
      };
    });
  } catch (error) {
    console.warn('IndexedDB not available:', error);
  }
}

/**
 * Get cached place status from IndexedDB
 * @param {string} placeId - The place ID
 * @param {number} maxAge - Maximum age in milliseconds (default 24 hours)
 * @returns {Promise<object|null>} - Cached status data or null
 */
export async function getCachedPlaceStatus(placeId, maxAge = 24 * 60 * 60 * 1000) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STATUS_STORE], 'readonly');
      const store = transaction.objectStore(STATUS_STORE);
      const request = store.get(placeId);

      request.onsuccess = () => {
        const result = request.result;
        if (!result) {
          resolve(null);
          return;
        }

        // Check if entry is expired
        const age = Date.now() - result.timestamp;
        if (age > maxAge) {
          resolve(null);
          return;
        }

        resolve(result.data);
      };

      request.onerror = () => {
        console.error('Error reading status from IndexedDB:', request.error);
        resolve(null);
      };
    });
  } catch (error) {
    console.warn('IndexedDB not available:', error);
    return null;
  }
}

/**
 * Store place status in IndexedDB cache
 * @param {string} placeId - The place ID
 * @param {object} data - The status data to cache
 * @returns {Promise<void>}
 */
export async function setCachedPlaceStatus(placeId, data) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STATUS_STORE], 'readwrite');
      const store = transaction.objectStore(STATUS_STORE);
      const request = store.put({
        placeId,
        data,
        timestamp: Date.now(),
      });

      request.onsuccess = () => resolve();
      request.onerror = () => {
        console.error('Error writing status to IndexedDB:', request.error);
        resolve();
      };
    });
  } catch (error) {
    console.warn('IndexedDB not available:', error);
  }
}

/**
 * Batch get multiple cached photo URLs
 * @param {string[]} placeIds - Array of place IDs
 * @param {number} maxAge - Maximum age in milliseconds
 * @returns {Promise<Map<string, string>>} - Map of placeId to URL
 */
export async function batchGetCachedPhotoUrls(placeIds, maxAge = 7 * 24 * 60 * 60 * 1000) {
  const results = new Map();

  try {
    const db = await openDB();
    const now = Date.now();

    return new Promise((resolve) => {
      const transaction = db.transaction([PHOTO_STORE], 'readonly');
      const store = transaction.objectStore(PHOTO_STORE);
      let completed = 0;

      placeIds.forEach(placeId => {
        const request = store.get(placeId);
        request.onsuccess = () => {
          completed++;
          const result = request.result;
          if (result && (now - result.timestamp) <= maxAge) {
            results.set(placeId, result.url);
          }
          if (completed === placeIds.length) {
            resolve(results);
          }
        };
        request.onerror = () => {
          completed++;
          if (completed === placeIds.length) {
            resolve(results);
          }
        };
      });

      // Handle empty array
      if (placeIds.length === 0) {
        resolve(results);
      }
    });
  } catch (error) {
    console.warn('IndexedDB batch get failed:', error);
    return results;
  }
}

/**
 * Batch store multiple photo URLs
 * @param {Array<{placeId: string, url: string}>} items - Array of items to cache
 * @returns {Promise<void>}
 */
export async function batchSetCachedPhotoUrls(items) {
  if (!items || items.length === 0) return;

  try {
    const db = await openDB();
    const now = Date.now();

    return new Promise((resolve) => {
      const transaction = db.transaction([PHOTO_STORE], 'readwrite');
      const store = transaction.objectStore(PHOTO_STORE);

      items.forEach(({ placeId, url }) => {
        store.put({
          placeId,
          url,
          timestamp: now,
        });
      });

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => {
        console.error('Error in batch write:', transaction.error);
        resolve();
      };
    });
  } catch (error) {
    console.warn('IndexedDB batch set failed:', error);
  }
}

/**
 * Clean up expired entries from all stores
 * Call this periodically (e.g., on app start) to prevent database bloat
 * @param {number} photoMaxAge - Max age for photos (default 30 days)
 * @param {number} statusMaxAge - Max age for status (default 7 days)
 */
export async function cleanupExpiredEntries(
  photoMaxAge = 30 * 24 * 60 * 60 * 1000,
  statusMaxAge = 7 * 24 * 60 * 60 * 1000
) {
  try {
    const db = await openDB();
    const now = Date.now();

    // Clean photo store
    const photoTransaction = db.transaction([PHOTO_STORE], 'readwrite');
    const photoStore = photoTransaction.objectStore(PHOTO_STORE);
    const photoIndex = photoStore.index('timestamp');

    const photoCursor = photoIndex.openCursor(IDBKeyRange.upperBound(now - photoMaxAge));
    photoCursor.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };

    // Clean status store
    const statusTransaction = db.transaction([STATUS_STORE], 'readwrite');
    const statusStore = statusTransaction.objectStore(STATUS_STORE);
    const statusIndex = statusStore.index('timestamp');

    const statusCursor = statusIndex.openCursor(IDBKeyRange.upperBound(now - statusMaxAge));
    statusCursor.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };
  } catch (error) {
    console.warn('IndexedDB cleanup failed:', error);
  }
}

/**
 * Get cache statistics
 * @returns {Promise<{photoCount: number, statusCount: number}>}
 */
export async function getCacheStats() {
  try {
    const db = await openDB();

    return new Promise((resolve) => {
      let photoCount = 0;
      let statusCount = 0;
      let completed = 0;

      const photoTx = db.transaction([PHOTO_STORE], 'readonly');
      const photoStore = photoTx.objectStore(PHOTO_STORE);
      const photoCountReq = photoStore.count();
      photoCountReq.onsuccess = () => {
        photoCount = photoCountReq.result;
        completed++;
        if (completed === 2) resolve({ photoCount, statusCount });
      };

      const statusTx = db.transaction([STATUS_STORE], 'readonly');
      const statusStore = statusTx.objectStore(STATUS_STORE);
      const statusCountReq = statusStore.count();
      statusCountReq.onsuccess = () => {
        statusCount = statusCountReq.result;
        completed++;
        if (completed === 2) resolve({ photoCount, statusCount });
      };
    });
  } catch (error) {
    return { photoCount: 0, statusCount: 0 };
  }
}

// ============================================
// Offline Lists & Places Functions
// ============================================

/**
 * Save a list for offline access
 * @param {Object} list - Full list object with items
 * @returns {Promise<void>}
 */
export async function saveOfflineList(list) {
  if (!list?.id) return;

  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([OFFLINE_LISTS_STORE], 'readwrite');
      const store = transaction.objectStore(OFFLINE_LISTS_STORE);
      const request = store.put({
        ...list,
        downloadedAt: Date.now(),
      });

      request.onsuccess = () => resolve();
      request.onerror = () => {
        console.error('Error saving offline list:', request.error);
        resolve();
      };
    });
  } catch (error) {
    console.warn('IndexedDB not available:', error);
  }
}

/**
 * Get a list from offline storage
 * @param {string} listId - The list ID to retrieve
 * @returns {Promise<Object|null>}
 */
export async function getOfflineList(listId) {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const transaction = db.transaction([OFFLINE_LISTS_STORE], 'readonly');
      const store = transaction.objectStore(OFFLINE_LISTS_STORE);
      const request = store.get(listId);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => {
        console.error('Error reading offline list:', request.error);
        resolve(null);
      };
    });
  } catch (error) {
    console.warn('IndexedDB not available:', error);
    return null;
  }
}

/**
 * Get all offline lists for a user
 * @param {string} userId - The user ID
 * @returns {Promise<Array>}
 */
export async function getAllOfflineLists(userId) {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const transaction = db.transaction([OFFLINE_LISTS_STORE], 'readonly');
      const store = transaction.objectStore(OFFLINE_LISTS_STORE);
      const index = store.index('userId');
      const request = index.getAll(userId);

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => {
        console.error('Error reading offline lists:', request.error);
        resolve([]);
      };
    });
  } catch (error) {
    console.warn('IndexedDB not available:', error);
    return [];
  }
}

/**
 * Delete a list from offline storage
 * @param {string} listId - The list ID to delete
 * @returns {Promise<void>}
 */
export async function deleteOfflineList(listId) {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const transaction = db.transaction([OFFLINE_LISTS_STORE], 'readwrite');
      const store = transaction.objectStore(OFFLINE_LISTS_STORE);
      const request = store.delete(listId);

      request.onsuccess = () => resolve();
      request.onerror = () => {
        console.error('Error deleting offline list:', request.error);
        resolve();
      };
    });
  } catch (error) {
    console.warn('IndexedDB not available:', error);
  }
}

/**
 * Save a place for offline access
 * @param {Object} place - Full place object
 * @returns {Promise<void>}
 */
export async function saveOfflinePlace(place) {
  if (!place?.placeId) return;

  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const transaction = db.transaction([OFFLINE_PLACES_STORE], 'readwrite');
      const store = transaction.objectStore(OFFLINE_PLACES_STORE);
      const request = store.put({
        ...place,
        downloadedAt: Date.now(),
      });

      request.onsuccess = () => resolve();
      request.onerror = () => {
        console.error('Error saving offline place:', request.error);
        resolve();
      };
    });
  } catch (error) {
    console.warn('IndexedDB not available:', error);
  }
}

/**
 * Batch save multiple places for offline access
 * @param {Array<Object>} places - Array of place objects
 * @returns {Promise<void>}
 */
export async function batchSaveOfflinePlaces(places) {
  if (!places || places.length === 0) return;

  try {
    const db = await openDB();
    const now = Date.now();

    return new Promise((resolve) => {
      const transaction = db.transaction([OFFLINE_PLACES_STORE], 'readwrite');
      const store = transaction.objectStore(OFFLINE_PLACES_STORE);

      places.forEach((place) => {
        if (place?.placeId) {
          store.put({
            ...place,
            downloadedAt: now,
          });
        }
      });

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => {
        console.error('Error in batch save places:', transaction.error);
        resolve();
      };
    });
  } catch (error) {
    console.warn('IndexedDB batch save places failed:', error);
  }
}

/**
 * Get a place from offline storage
 * @param {string} placeId - The place ID to retrieve
 * @returns {Promise<Object|null>}
 */
export async function getOfflinePlace(placeId) {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const transaction = db.transaction([OFFLINE_PLACES_STORE], 'readonly');
      const store = transaction.objectStore(OFFLINE_PLACES_STORE);
      const request = store.get(placeId);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => {
        console.error('Error reading offline place:', request.error);
        resolve(null);
      };
    });
  } catch (error) {
    console.warn('IndexedDB not available:', error);
    return null;
  }
}

/**
 * Save offline metadata (e.g., last sync time, storage info)
 * @param {string} key - Metadata key
 * @param {Object} value - Metadata value
 * @returns {Promise<void>}
 */
export async function setOfflineMeta(key, value) {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const transaction = db.transaction([OFFLINE_META_STORE], 'readwrite');
      const store = transaction.objectStore(OFFLINE_META_STORE);
      const request = store.put({ key, ...value, updatedAt: Date.now() });

      request.onsuccess = () => resolve();
      request.onerror = () => {
        console.error('Error saving offline meta:', request.error);
        resolve();
      };
    });
  } catch (error) {
    console.warn('IndexedDB not available:', error);
  }
}

/**
 * Get offline metadata
 * @param {string} key - Metadata key
 * @returns {Promise<Object|null>}
 */
export async function getOfflineMeta(key) {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const transaction = db.transaction([OFFLINE_META_STORE], 'readonly');
      const store = transaction.objectStore(OFFLINE_META_STORE);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => {
        console.error('Error reading offline meta:', request.error);
        resolve(null);
      };
    });
  } catch (error) {
    console.warn('IndexedDB not available:', error);
    return null;
  }
}

/**
 * Get extended cache statistics including offline data
 * @returns {Promise<Object>}
 */
export async function getOfflineStats() {
  try {
    const db = await openDB();

    return new Promise((resolve) => {
      const stats = {
        photoCount: 0,
        statusCount: 0,
        offlineListCount: 0,
        offlinePlaceCount: 0,
      };
      let completed = 0;
      const total = 4;

      const checkComplete = () => {
        completed++;
        if (completed === total) resolve(stats);
      };

      // Photo count
      const photoTx = db.transaction([PHOTO_STORE], 'readonly');
      const photoStore = photoTx.objectStore(PHOTO_STORE);
      const photoCountReq = photoStore.count();
      photoCountReq.onsuccess = () => {
        stats.photoCount = photoCountReq.result;
        checkComplete();
      };
      photoCountReq.onerror = checkComplete;

      // Status count
      const statusTx = db.transaction([STATUS_STORE], 'readonly');
      const statusStore = statusTx.objectStore(STATUS_STORE);
      const statusCountReq = statusStore.count();
      statusCountReq.onsuccess = () => {
        stats.statusCount = statusCountReq.result;
        checkComplete();
      };
      statusCountReq.onerror = checkComplete;

      // Offline lists count
      const listsTx = db.transaction([OFFLINE_LISTS_STORE], 'readonly');
      const listsStore = listsTx.objectStore(OFFLINE_LISTS_STORE);
      const listsCountReq = listsStore.count();
      listsCountReq.onsuccess = () => {
        stats.offlineListCount = listsCountReq.result;
        checkComplete();
      };
      listsCountReq.onerror = checkComplete;

      // Offline places count
      const placesTx = db.transaction([OFFLINE_PLACES_STORE], 'readonly');
      const placesStore = placesTx.objectStore(OFFLINE_PLACES_STORE);
      const placesCountReq = placesStore.count();
      placesCountReq.onsuccess = () => {
        stats.offlinePlaceCount = placesCountReq.result;
        checkComplete();
      };
      placesCountReq.onerror = checkComplete;
    });
  } catch (error) {
    console.warn('Failed to get offline stats:', error);
    return {
      photoCount: 0,
      statusCount: 0,
      offlineListCount: 0,
      offlinePlaceCount: 0,
    };
  }
}

/**
 * Clear all offline data (lists, places, meta)
 * Used when user logs out or wants to free up storage
 * @returns {Promise<void>}
 */
export async function clearAllOfflineData() {
  try {
    const db = await openDB();

    return new Promise((resolve) => {
      let completed = 0;
      const total = 3;

      const checkComplete = () => {
        completed++;
        if (completed === total) resolve();
      };

      // Clear offline lists
      const listsTx = db.transaction([OFFLINE_LISTS_STORE], 'readwrite');
      const listsStore = listsTx.objectStore(OFFLINE_LISTS_STORE);
      const listsClearReq = listsStore.clear();
      listsClearReq.onsuccess = checkComplete;
      listsClearReq.onerror = checkComplete;

      // Clear offline places
      const placesTx = db.transaction([OFFLINE_PLACES_STORE], 'readwrite');
      const placesStore = placesTx.objectStore(OFFLINE_PLACES_STORE);
      const placesClearReq = placesStore.clear();
      placesClearReq.onsuccess = checkComplete;
      placesClearReq.onerror = checkComplete;

      // Clear offline meta
      const metaTx = db.transaction([OFFLINE_META_STORE], 'readwrite');
      const metaStore = metaTx.objectStore(OFFLINE_META_STORE);
      const metaClearReq = metaStore.clear();
      metaClearReq.onsuccess = checkComplete;
      metaClearReq.onerror = checkComplete;
    });
  } catch (error) {
    console.warn('Failed to clear offline data:', error);
  }
}

/**
 * Check if a list is available offline
 * @param {string} listId - The list ID to check
 * @returns {Promise<boolean>}
 */
export async function isListOffline(listId) {
  const list = await getOfflineList(listId);
  return !!list;
}
